import { Box, Button, Container, Grid, Typography } from "@mui/material";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import {
  getGameSystems,
  handlePagingAll,
} from "../../components/api/APIUtilities";
import {
  BCPCalendarDay,
  BCPSearch,
  BCPSquarePlus,
} from "../../components/icons/bcp/BCPIcons";
import InfoContainer from "../../components/layout/InfoContainer";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { postNotification } from "../../components/utilities/Notifications";
import { isAppMode } from "../../global/AppOverlay";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { mapData } from "../events/utils/event";
import PlayDashEvents from "./PlayDashboard/PlayDashEvents";
// import PlayDashOrganizations from "./PlayDashboard/PlayDashOrganizations";

const searchLimit = 30;
export default function PlayDashboard(props) {
  const authContext = useContext(AuthContext);
  const appMode = isAppMode();

  const [findEvents, setFindEvents] = useState(null);
  const [myEvents, setMyEvents] = useState(null);

  const [geoLocation, setGeoLocation] = useState(null);
  const [searchValues, setSearchValues] = useState(null);

  const [findEventsLoading, setFindEventsLoading] = useState(true);
  const [myEventsLoading, setMyEventsLoading] = useState(true);

  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystemDict, setGameSystemDict] = useState({});

  const getEventSearchValues = async () => {
    const tempSearchValues = {
      limit: searchLimit,
      startDate: moment()
        .subtract(3, "days")
        .startOf("day")
        .format("YYYY-MM-DD"),
      endDate: moment().add(2, "months").endOf("month").format("YYYY-MM-DD"),
      location: "Any Location",
      distance: 100,
      distanceType: "miles",

      sortType: "Start Date Ascending",
      sortKey: "eventDate",
      sortAscending: true,
    };

    try {
      if ("geolocation" in navigator) {
        tempSearchValues.location = "Current Location";
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setGeoLocation(position.coords);
          },
          (err) => {
            tempSearchValues.location = "Any Location";
            setGeoLocation("none");
          }
        );
      } else {
        tempSearchValues.location = "Any Location";
        setGeoLocation("none");
      }
    } catch (err) {
      postNotification("error", err);
      setGeoLocation("none");
      tempSearchValues.location = "Any Location";
    } finally {
      setSearchValues(tempSearchValues);
    }
  };

  const getFindEvents = async (searchValues, location) => {
    const queryStringParameters = {
      ...searchValues,
    };

    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      queryStringParameters.gameSystemId =
        getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    if (queryStringParameters.location === "Current Location" && location) {
      queryStringParameters.location = JSON.stringify({
        distance: queryStringParameters.distance,
        center: {
          lat: location.latitude,
          long: location.longitude,
        },
      });
    } else {
      delete queryStringParameters.location;
      delete queryStringParameters.distance;
      delete queryStringParameters.distanceType;
    }

    try {
      const eventData = await handlePagingAll("/events", queryStringParameters);

      setFindEvents(eventData);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setFindEventsLoading(false);
    }
  };

  // Gets my upcoming events
  const getMyEvents = async () => {
    let queryStringParameters = {};
    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      queryStringParameters.gameSystemId =
        getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    // Current Tab

    try {
      let _gameSystems = gameSystems;
      if (!_gameSystems) {
        _gameSystems = await getGameSystems({
          inclSystem: true,
        });
        setGameSystems(_gameSystems);
      }

      let _gameSystemDict = gameSystemDict;
      if (!_gameSystemDict && typeof gameSystemDict === "object") {
        _gameSystemDict = _gameSystems.forEach((gameSystem) => {
          gameSystemDict[gameSystem.id] = gameSystem;
        });
        setGameSystemDict(_gameSystemDict);
      }

      let eventData = await handlePagingAll("/events", {
        ...queryStringParameters,
        limit: searchLimit,
        playerEvents: true,
        toEvents: true,
        eventEndDateFrom: moment().add(-3, "days").format("YYYY-MM-DDTHH:mm"),
      });

      eventData = mapData(eventData, gameSystemDict, moment);

      setMyEvents(eventData);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setMyEventsLoading(false);
    }
  };

  useEffect(() => {
    getEventSearchValues();
    getMyEvents();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (geoLocation !== null && searchValues !== null) {
      getFindEvents(searchValues, geoLocation === "none" ? null : geoLocation);
    }
    // eslint-disable-next-line
  }, [geoLocation, searchValues]);

  return (
    <Box width="100%">
      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        <Grid container sx={{ p: 2, gap: 2 }}>
          <Grid
            item
            xs={12}
            sx={{
              display: { xs: "block", md: "flex" },
              justifyContent: "space-between",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="h2"
                  component="h3"
                  fontWeight="700"
                  gutterBottom
                  sx={{ mb: 4 }}
                >
                  {`Welcome back${
                    authContext?.authState?.identity?.nickname ||
                    authContext?.authState?.identity?.firstName
                      ? ", " +
                        (authContext?.authState?.identity?.nickname ||
                          authContext?.authState?.identity?.firstName)
                      : ""
                  }!`}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <PlayDashEvents
                  events={findEvents}
                  loading={findEventsLoading}
                  gameSystems={gameSystems}
                  title="Find Events"
                  zoom={8}
                  HeaderButton={
                    <Button
                      variant="contained"
                      href="/play/events"
                      startIcon={<BCPSearch />}
                      fullWidth
                    >
                      Find Events
                    </Button>
                  }
                  NoResultsPanel={
                    <InfoContainer
                      title="No nearby events found."
                      body="There were no events found near you. Check back later or try searching for events in a different location."
                      ActionButton={
                        <Button
                          variant="contained"
                          href="/play/events"
                          target={`${appMode ? "_blank" : "_self"}`}
                          startIcon={<BCPSearch />}
                        >
                          Search For Events
                        </Button>
                      }
                    />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <PlayDashEvents
                  events={myEvents}
                  loading={myEventsLoading}
                  gameSystems={gameSystems}
                  title="My Upcoming Events"
                  zoom={10}
                  HeaderButton={
                    <Button
                      variant="contained"
                      href="/play/my-events"
                      startIcon={<BCPCalendarDay />}
                      fullWidth
                    >
                      View All My Events
                    </Button>
                  }
                  NoResultsPanel={
                    <InfoContainer
                      title="No upcoming events found."
                      body="You have no upcoming events. Sign up for one or create an event to get started."
                      ActionButton={
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            href="/play/events"
                            target={`${appMode ? "_blank" : "_self"}`}
                            startIcon={<BCPSearch />}
                          >
                            Search For Events
                          </Button>
                          <Button
                            variant="outlined"
                            href="/organize/event"
                            target={`${appMode ? "_blank" : "_self"}`}
                            startIcon={<BCPSquarePlus />}
                          >
                            Create An Event
                          </Button>
                        </Box>
                      }
                    />
                  }
                />
              </Grid>

              {/* TODO: Uncommend when component is built */}
              {/* <Grid item xs={12}>
                <PlayDashOrganizations />
              </Grid> */}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
